/**
 * Executor Framework
 * Base interface and registry for multi-agent executors
 */

import type { ExecutorType, ExecutorOptions, ExecutorResult, ExecutorConfig, AvailabilityStatus } from '../types.js';

/**
 * Base interface for all agent executors
 */
export interface AgentExecutor {
    /** Executor type identifier */
    readonly type: ExecutorType;

    /** Check if the executor is available on this system (shallow check) */
    isAvailable(): Promise<boolean>;

    /** Execute a prompt with this executor */
    execute(prompt: string, options?: ExecutorOptions): Promise<ExecutorResult>;

    /** Abort current execution */
    abort(): void;

    /** Deep availability check with auth status (optional) */
    checkAvailability?(): Promise<AvailabilityStatus>;
}

/**
 * Registry for managing multiple executors
 */
export class ExecutorRegistry {
    private executors: Map<ExecutorType, AgentExecutor> = new Map();
    private configs: Map<ExecutorType, ExecutorConfig> = new Map();

    /**
     * Register an executor
     */
    register(executor: AgentExecutor, config?: ExecutorConfig): void {
        this.executors.set(executor.type, executor);
        if (config) {
            this.configs.set(executor.type, config);
        }
    }

    /**
     * Get an executor by type
     */
    get(type: ExecutorType): AgentExecutor | undefined {
        return this.executors.get(type);
    }

    /**
     * Get executor configuration
     */
    getConfig(type: ExecutorType): ExecutorConfig | undefined {
        return this.configs.get(type);
    }

    /**
     * Check if an executor is registered and enabled
     */
    isEnabled(type: ExecutorType): boolean {
        const config = this.configs.get(type);
        return config?.enabled !== false;
    }

    /**
     * Get all available executors (installed and enabled)
     */
    async getAvailable(): Promise<ExecutorType[]> {
        const available: ExecutorType[] = [];

        for (const [type, executor] of this.executors) {
            if (!this.isEnabled(type)) continue;

            try {
                const isAvail = await executor.isAvailable();
                if (isAvail) {
                    available.push(type);
                }
            } catch {
                // Not available
            }
        }

        return available;
    }

    /**
     * Get all registered executor types
     */
    getRegistered(): ExecutorType[] {
        return Array.from(this.executors.keys());
    }

    /**
     * Execute with a specific executor
     */
    async execute(
        type: ExecutorType,
        prompt: string,
        options?: ExecutorOptions
    ): Promise<ExecutorResult> {
        const executor = this.executors.get(type);
        if (!executor) {
            return {
                success: false,
                output: '',
                error: `Executor '${type}' not registered`,
                durationMs: 0,
            };
        }

        const available = await executor.isAvailable();
        if (!available) {
            return {
                success: false,
                output: '',
                error: `Executor '${type}' is not available on this system`,
                durationMs: 0,
            };
        }

        // Merge with config defaults
        const config = this.configs.get(type);
        const mergedOptions: ExecutorOptions = {
            timeout: config?.timeout,
            args: config?.defaultArgs,
            ...options,
        };

        return executor.execute(prompt, mergedOptions);
    }
}

// Export singleton registry
let registryInstance: ExecutorRegistry | null = null;

export function getExecutorRegistry(): ExecutorRegistry {
    if (!registryInstance) {
        registryInstance = new ExecutorRegistry();
    }
    return registryInstance;
}

export function resetExecutorRegistry(): void {
    registryInstance = null;
}
